import { useState, useEffect, useRef } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MRGE_COUNTRIES, MRGE_CATEGORIES, MRGE_CATEGORY_LABELS } from "./constants";

/**
 * Self-contained mrge / OneCPA import modal.
 *
 * Two paths:
 *   1. Sync import   — bounded by `max_brands`. Returns the row count
 *                      synchronously; modal stays open on truncation
 *                      so admin can re-run with narrower filters.
 *   2. Background    — queues a full-catalog job, polls every 4s, and
 *                      reports progress (pages fetched, advertisers
 *                      so far, rates fetched). Stops polling at the
 *                      first terminal status (completed | failed) or
 *                      after a 10 min hard ceiling.
 *
 * The "Importing… 12s" elapsed counter on the sync button keeps the
 * UX alive while the synchronous call is in flight (otherwise the
 * spinner looks frozen on bigger catalogs).
 */
export default function MrgeImportDialog({ open, onClose, onImported }) {
  const [country, setCountry] = useState("US");
  const [search, setSearch] = useState("");
  const [categories, setCategories] = useState([]);
  const [maxBrands, setMaxBrands] = useState(2000);
  const [fetchRates, setFetchRates] = useState(false);
  const [importing, setImporting] = useState(false);
  const [bgRun, setBgRun] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const startedAtRef = useRef(0);

  // Live "Importing… Xs" counter so the spinner never looks dead.
  useEffect(() => {
    if (!importing) {
      setElapsed(0);
      startedAtRef.current = 0;
      return undefined;
    }
    if (!startedAtRef.current) startedAtRef.current = Date.now();
    const id = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [importing]);

  if (!open) return null;

  const buildBody = (forBg) => {
    const body = {
      countries: [country],
      max_brands: forBg ? 100000 : (Number(maxBrands) || 2000),
      fetch_rates: !!fetchRates,
    };
    if (search.trim()) body.search = search.trim();
    if (categories.length > 0) body.categories = categories;
    return body;
  };

  const importSync = async () => {
    setImporting(true);
    try {
      const { data } = await API.post("/admin/mrge/brands/import", buildBody(false));
      const ratesNote = data.with_rates !== undefined && fetchRates
        ? ` · ${data.with_rates} with rates`
        : "";
      const truncNote = data.truncated
        ? ` · ⚠️ truncated at ${data.imported} (more available — narrow filters or use Background)`
        : "";
      const deactNote = data.deactivated_brands
        ? ` · ${data.deactivated_brands} deactivated`
        : "";
      toast.success(
        `mrge import · ${data.imported} brands (${data.new} new)${ratesNote}${truncNote}${deactNote}` +
          (search ? ` · search: "${search}"` : "") +
          (categories.length ? ` · ${categories.length} category filter(s)` : "")
      );
      if (!data.truncated) {
        onClose();
        setSearch("");
        setCategories([]);
      }
      onImported?.();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Failed to import mrge brands";
      toast.error(typeof detail === "string" ? detail : "Failed to import mrge brands");
    } finally {
      setImporting(false);
    }
  };

  const importBackground = async () => {
    setImporting(true);
    try {
      const { data } = await API.post("/admin/mrge/brands/import/start", buildBody(true));
      setBgRun({ run_id: data.run_id, status: "queued" });
      toast.success(`mrge background import queued · run ${data.run_id.slice(0, 8)}…`);

      const startedAt = Date.now();
      const pollId = setInterval(async () => {
        try {
          const { data: r } = await API.get(`/admin/mrge/brands/import/runs/${data.run_id}`);
          setBgRun(r);
          if (r.status === "completed") {
            clearInterval(pollId);
            const res = r.result || {};
            toast.success(`mrge bg import done · ${res.imported || 0} brands (${res.new || 0} new)`);
            onImported?.();
          } else if (r.status === "failed") {
            clearInterval(pollId);
            toast.error(`mrge bg import failed · ${r.error || "unknown"}`);
          } else if (Date.now() - startedAt > 10 * 60 * 1000) {
            clearInterval(pollId);
            toast.error("mrge bg import polling timed out — check Import Runs panel");
          }
        } catch {
          // transient — keep polling
        }
      }, 4000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start mrge background import");
    } finally {
      setImporting(false);
    }
  };

  const handleCancel = () => {
    onClose();
    setSearch("");
    setCategories([]);
    setBgRun(null);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="mrge-import-overlay"
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-xl p-6 space-y-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Import mrge / OneCPA Brands</h3>
          <p className="text-xs text-slate-500 mt-1">
            Pull advertisers from mrge's catalog filtered by country, categories, and optional keyword. Commission rates auto-fetched.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Country</label>
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              disabled={importing}
              data-testid="mrge-country-select"
              className="w-full h-9 text-sm border border-slate-200 rounded-md px-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]/20"
            >
              {MRGE_COUNTRIES.map((cc) => (
                <option key={cc} value={cc}>{cc}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
              Search keyword <span className="text-slate-400 normal-case font-normal">(optional)</span>
            </label>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="e.g. apple, vpn, finance"
              className="h-9 text-sm"
              disabled={importing}
              data-testid="mrge-search-input"
              onKeyDown={(e) => { if (e.key === "Enter") importSync(); }}
            />
          </div>
        </div>

        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
              Categories <span className="text-slate-400 normal-case font-normal">(optional, multi-select)</span>
            </label>
            {categories.length > 0 && (
              <button
                type="button"
                onClick={() => setCategories([])}
                className="text-[10px] text-slate-500 hover:text-slate-800 underline"
              >
                Clear ({categories.length})
              </button>
            )}
          </div>
          <div
            className="grid grid-cols-2 gap-1.5 max-h-[240px] overflow-y-auto border border-slate-200 rounded-md p-2 bg-slate-50/40"
            data-testid="mrge-categories-grid"
          >
            {MRGE_CATEGORIES.map((cat) => {
              const checked = categories.includes(cat);
              return (
                <label
                  key={cat}
                  className={`flex items-center gap-2 text-xs cursor-pointer px-2 py-1.5 rounded transition-colors ${
                    checked ? "bg-emerald-50 text-emerald-900" : "hover:bg-white text-slate-700"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setCategories([...categories, cat]);
                      } else {
                        setCategories(categories.filter((x) => x !== cat));
                      }
                    }}
                    disabled={importing}
                    data-testid={`mrge-cat-${cat}`}
                    className="h-3.5 w-3.5 accent-emerald-600"
                  />
                  <span>{MRGE_CATEGORY_LABELS[cat] || cat}</span>
                </label>
              );
            })}
          </div>
          <p className="text-[11px] text-slate-500">
            {categories.length === 0
              ? "No category filter — pulls every advertiser in selected country."
              : `${categories.length} categor${categories.length === 1 ? "y" : "ies"} selected — only matching advertisers will be imported.`}
          </p>
        </div>

        <p className="text-[11px] text-amber-700">
          ⚠️ Broad categories (TRAVEL, SHOPPING) on US can return 25K+ brands. The sync import is capped by <strong>Max brands</strong>; use <strong>Background (full catalog)</strong> if you need everything.
        </p>

        <div className="grid grid-cols-2 gap-3 pt-2">
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Max brands (sync)</label>
            <Input
              type="number"
              min={1}
              max={50000}
              value={maxBrands}
              onChange={(e) => setMaxBrands(e.target.value)}
              disabled={importing}
              data-testid="mrge-max-brands-input"
              className="h-9 text-sm"
            />
          </div>
          <label className="flex items-end gap-2 text-xs text-slate-700 cursor-pointer pb-2" data-testid="mrge-fetch-rates-label">
            <input
              type="checkbox"
              checked={fetchRates}
              onChange={(e) => setFetchRates(e.target.checked)}
              disabled={importing}
              data-testid="mrge-fetch-rates-checkbox"
              className="h-3.5 w-3.5 accent-emerald-600"
            />
            Fetch commission rates inline (slower — adds 100ms / brand)
          </label>
        </div>

        {bgRun && (
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs space-y-1" data-testid="mrge-bg-run-panel">
            <div className="flex items-center justify-between">
              <span className="font-mono text-slate-500">run {bgRun.run_id?.slice(0, 8)}…</span>
              <span
                className={
                  bgRun.status === "completed"
                    ? "text-emerald-700 font-semibold"
                    : bgRun.status === "failed"
                    ? "text-rose-700 font-semibold"
                    : "text-slate-600"
                }
                data-testid="mrge-bg-run-status"
              >
                {bgRun.stage || bgRun.status}
              </span>
            </div>
            {(bgRun.advertisers_so_far !== undefined || bgRun.pages_fetched !== undefined) &&
              bgRun.status !== "completed" &&
              bgRun.status !== "failed" && (
                <div className="text-slate-700" data-testid="mrge-bg-run-progress">
                  {bgRun.pages_fetched ?? 0} pages ·{" "}
                  {(bgRun.advertisers_so_far ?? 0).toLocaleString()} advertisers fetched
                  {bgRun.rates_fetched ? ` · ${bgRun.rates_fetched} rates` : ""}
                </div>
              )}
            {bgRun.result && (
              <div className="text-slate-700">
                {bgRun.result.imported} brands · {bgRun.result.new} new
                {bgRun.result.with_rates ? ` · ${bgRun.result.with_rates} with rates` : ""}
              </div>
            )}
            {bgRun.error && (
              <div className="text-rose-700 font-mono">{bgRun.error}</div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={importing}
            data-testid="mrge-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={importBackground}
            disabled={importing}
            data-testid="mrge-bg-import-btn"
            className="border-amber-300 text-amber-800 hover:bg-amber-50"
          >
            Background (full catalog)
          </Button>
          <Button
            onClick={importSync}
            disabled={importing}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="mrge-confirm-import-btn"
          >
            {importing ? `Importing… ${elapsed}s` : "Import"}
          </Button>
        </div>
      </div>
    </div>
  );
}
